import { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import moment, { type Moment } from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  dimensionRowLimitSelector,
  realTimeModeOnTrendsSelector,
  getFirstAppliedFilter,
  filterSelector,
} from '@store/common/selectors';
import { DATASET, isAppModule } from '@utils/constants';
import { useSelector } from 'react-redux';
import { flattenData } from '@components/dimension-panel-hz/hooks/useFetchGroupBy';
import { get } from 'lodash';
import { getDistributionData } from '@api/metricContainer';
import { arddDimension, arddMetric, groupByQuery } from '../constants';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import getQueryTag from '@utils/misc/queryTag';
import { formatQueryStatus } from './utils';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { drillDownFiltersAtom } from '@components/app-analyse-network-request/utils/atom';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';

export function useDurationRoot({
  enabled = true,
  interval,
  keepPreviousData = false,
  enableSSF = false,
  withTotals,
}) {
  const globalFilter = useSelector(filterSelector);
  const drillDownFilters = useAtomValue(drillDownFiltersAtom);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const globalMetricFilter = useSelector(metricFilterRulesSelector);
  const enableMetricFilter = enableSSF && !!globalMetricFilter.length;
  const { dataScope, usecaseKey }: any = useSelector(activeDashboardSelector);
  // build query
  const query = useMemo(() => {
    const filter = [...globalFilter];
    const q = {
      dataset: DATASET,
      filter,
      metrics: [arddMetric],
      dimension: arddDimension,
      interval: `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`,
      isLiveMode: realTimeOnTrends,
      queryGranularity: 'ALL',
      options: {
        withTotals: true,
      },
    } as any;
    const resDrillDownFilters = [...drillDownFilters];
    if (enableMetricFilter) {
      q.metricFilter = globalMetricFilter;
      q.drillDownFilters = resDrillDownFilters;
      q.options.useSamplingForEventAnalysis = true;
      q.options.samplingAsDataset = true;
    }
    if (isAppModule) {
      q.options.dataScope = dataScope;
      q.options.presetKey = usecaseKey;
    }
    if (withTotals !== undefined) {
      q.options.withTotals = withTotals;
    }
    return q;
  }, [
    drillDownFilters,
    enableMetricFilter,
    globalMetricFilter,
    globalFilter,
    interval,
    dataScope,
    usecaseKey,
    realTimeOnTrends,
    withTotals,
  ]);

  const { data, ...queryStatus } = useQuery(
    ['app-network-distribution', query],
    () => {
      return getDistributionData(query).then((response) => {
        return {
          row: get(response, ['data', 'data', '0', 'result'], []),
          total: get(response, ['data', 'totals', '0', 'result'], {}),
        };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
      keepPreviousData,
    },
  );

  return {
    ...formatQueryStatus({ ...queryStatus, isEmpty: !data?.row?.length }),
    data: data?.row,
    total: data?.total,
  };
}

export function useDimensionGroupBy({
  dimension,
  metrics,
  sortByMetric,
  metricFilters = [],
  enabled = true,
  filters = [],
  select,
  interval,
  keepPreviousData = false,
  enableSSF = false,
  withTotals,
}: {
  dimension: string;
  metrics: string[];
  sortByMetric: string;
  keepPreviousData: boolean;
  enabled?: boolean;
  enableSSF?: boolean;
  filters?: any[];
  metricFilters?: any[];
  select?: (data: any) => any;
  interval: { from: Moment; to: Moment };
  withTotals?: boolean;
}) {
  const globalFilter = useSelector(getFirstAppliedFilter);
  const secondaryDrillDownFilters = useAtomValue(drillDownFiltersAtom);
  const globalMetricFilter = useSelector(metricFilterRulesSelector);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const { limit } = useSelector(dimensionRowLimitSelector);
  const enableMetricFilter = enableSSF && !!globalMetricFilter.length;
  const { dataScope, usecaseKey }: any = useSelector(activeDashboardSelector);

  const innerDrillDownFilters = useMemo(() => {
    return [...filters, ...metricFilters];
  }, [filters, metricFilters]);

  const query = useMemo(() => {
    const q: any = {
      ...cloneDeep(groupByQuery),
      interval: `${moment(interval.from).format()}/${moment(interval.to).format()}`,
      filter: [...(globalFilter.rule || []), ...(!enableMetricFilter ? innerDrillDownFilters : [])],
      metrics,
      groupBy: [dimension],
      limit,
      isLiveMode: realTimeOnTrends,
    };
    if (realTimeOnTrends) {
      q.ignoreInterval = true;
    }
    q.sortBy[0].metric = sortByMetric;
    if (enableMetricFilter) {
      q.metricFilter = globalMetricFilter;
      q.drillDownFilters = [...secondaryDrillDownFilters, ...innerDrillDownFilters]; // if enable metric filter, we need to pass drillDownFilters to another field instead of the global filters
      q.options.useSamplingForEventAnalysis = true;
      q.options.samplingAsDataset = true;
    }
    if (isAppModule) {
      q.options.dataScope = dataScope;
      q.options.presetKey = usecaseKey;
    }
    if (withTotals !== undefined) {
      q.options.withTotals = withTotals;
    }
    return q;
  }, [
    globalMetricFilter,
    secondaryDrillDownFilters,
    innerDrillDownFilters,
    enableMetricFilter,
    dimension,
    interval,
    limit,
    metrics,
    realTimeOnTrends,
    globalFilter,
    dataScope,
    usecaseKey,
    sortByMetric,
    withTotals,
  ]);

  const queryTag = getQueryTag({ liveMode: realTimeOnTrends });

  const { data, ...queryStatus } = useQuery(
    ['app-network-dimension-groupby', query],
    () => {
      return getDimensionData({ queries: [query] }, { headers: { ...queryTag } }, instance).then(
        (response) => {
          return flattenData(response.data, query);
        },
      );
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
      keepPreviousData,
    },
  );

  const formattedData = useMemo(() => {
    return select ? select((data as any)?.rows) : (data as any)?.rows;
  }, [data, select]);

  return {
    ...formatQueryStatus({ ...queryStatus, isEmpty: !(data as any)?.rows?.length }),
    data: formattedData,
    total: (data as any)?.totals,
  };
}
